import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TierDiscountStrip from "../components/TierDiscountStrip";
import SEO, { LOCAL_BUSINESS_JSONLD } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IndianOutfitHireMelbourne() {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/outfits`)
      .then(r => r.json())
      .then(d => setOutfits(d.filter(o => o.available).slice(0, 12)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        rawTitle="Indian Outfit Hire Melbourne – Rent Premium Lehengas, Sarees & More | Mahra"
        description="Hire premium Indian outfits in Melbourne. Lehengas, sarees, anarkalis and accessories for weddings, engagements and cultural events. Free pickup from Oakleigh South."
        path="/indian-outfit-hire-melbourne"
        jsonLd={LOCAL_BUSINESS_JSONLD}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Indian Outfit Hire Melbourne" },
        ]}
      />
      <Navbar />
      <TierDiscountStrip />

      {/* Hero Section */}
      <section className="bg-white border-b border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-[12px] mb-6" data-testid="seo-breadcrumb">
            <Link to="/" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Home</Link>
            <span className="text-[#676664]/30">/</span>
            <span className="text-[#282828] font-medium">Indian Outfit Hire Melbourne</span>
          </nav>
          <h1 className="text-3xl lg:text-5xl text-[#282828] font-light leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="seo-page-h1">
            Indian Outfit Hire <span className="italic">in Melbourne</span>
          </h1>
          <p className="text-[14px] text-[#676664] leading-relaxed max-w-2xl">
            Discover Mahra's curated collection of premium Indian outfits available for hire across Melbourne. From bridal lehengas to elegant sarees, find the perfect piece for your celebration.
          </p>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10">
          <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Browse Our Collection</h2>
          <p className="text-[13px] text-[#676664] mb-8">Explore our range of Indian outfits available for rent in Melbourne.</p>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="bg-[#E8E0D5] animate-pulse" style={{ paddingBottom: '133%' }} />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="seo-product-grid">
              {outfits.map(outfit => (
                <ProductCard key={outfit.id} outfit={outfit} />
              ))}
            </div>
          )}
          <div className="mt-8 text-center">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-[11px] text-[#282828] tracking-[0.2em] uppercase border-b border-[#282828] pb-1 hover:opacity-70 transition-opacity" data-testid="view-full-collection-link">
              View Full Collection <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="border-t border-[#282828]/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <div className="text-[13px] text-[#676664] leading-relaxed space-y-4">
              <p>Looking for <Link to="/catalog" className="text-[#9A7B4F] hover:underline">Indian outfit hire</Link> in Melbourne? Mahra offers a premium rental experience for those who want to wear exceptional Indian fashion without the full purchase price. Our carefully curated collection features pieces sourced from leading ateliers, each maintained to the highest standard and ready for your next celebration.</p>

              <p><strong className="text-[#282828] font-medium">A Collection for Every Occasion</strong></p>
              <p>Whether you need a show-stopping <Link to="/lehenga-hire-melbourne" className="text-[#9A7B4F] hover:underline">lehenga for hire</Link> for a Melbourne wedding, a graceful <Link to="/saree-rental-melbourne" className="text-[#9A7B4F] hover:underline">saree for rent</Link> for a temple visit, or a contemporary indo-western piece for an engagement party, our collection has you covered. Every outfit in our wardrobe has been selected for its quality, craftsmanship, and ability to make you feel extraordinary.</p>

              <p><strong className="text-[#282828] font-medium">Simple, Transparent Rental Process</strong></p>
              <p>Hiring an Indian outfit from Mahra takes just a few minutes. Browse our collection online, choose your event dates, and select a 4-day or 8-day rental window. Each piece arrives freshly dry-cleaned and ready to wear. After your event, simply return it — we handle the rest.</p>

              <p><strong className="text-[#282828] font-medium">Melbourne-Wide Service</strong></p>
              <p>Based in Oakleigh South, we offer free local pickup and delivery across Melbourne for orders over $175. Our clients come from every corner of the city — from Carlton to Cranbourne, Doncaster to Dandenong — for weddings, Diwali celebrations, Eid gatherings, and every cultural occasion in between.</p>

              <p><strong className="text-[#282828] font-medium">Save More with Bundle Hire</strong></p>
              <p>Complete your look by hiring multiple pieces together. Our <Link to="/build-your-look" className="text-[#9A7B4F] hover:underline">Build Your Look</Link> feature lets you pair outfits with jewellery, clutches, and accessories — with bundle discounts of up to 20%. It's the most stylish and affordable way to dress for your Melbourne event.</p>

              <p>Browse our full range of <Link to="/catalog" className="text-[#9A7B4F] hover:underline">premium Indian outfits for hire</Link> or explore our curated <Link to="/looks" className="text-[#9A7B4F] hover:underline">styled looks</Link> for instant outfit inspiration.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="border-t border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-10">
          <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-4">Explore More</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/lehenga-hire-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-lehenga">Lehenga Hire Melbourne</Link>
            <Link to="/saree-rental-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-saree">Saree Rental Melbourne</Link>
            <Link to="/looks" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-looks">Curated Looks</Link>
            <Link to="/build-your-look" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-build">Build Your Look</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
